import { DictationStableTextManagerMapValue } from '../useTextProcessing';

// attempt=1 -> 0s, attempt=2 -> 1s, attempt=3 -> 2s, attempt=4 -> 4s, ...
// Capped at 16s. Returns milliseconds.
export const getBackoffDelayMs = (attempt: number): number => {
  if (attempt <= 1) return 0; // first retry immediate
  return Math.min(1000 * 2 ** (attempt - 2), 16000); // 1,2,4,8,16 capped at 16
};

/**
 * Returns the selectionRef belonging to the map entry whose `audioOffsetMs`
 * is closest to the audio marker duration (audioMarkerEnd - audioMarkerStart).
 *
 * If there are multiple equally-close candidates, the one with the smaller map key wins.
 * Returns null for an empty map.
 */
export const getClosestEntryId = (
  managerMap: Map<string, DictationStableTextManagerMapValue>,
  audioMarkerStart: number,
  audioMarkerEnd: number
): Nullable<string> => {
  if (managerMap.size === 0) return null;

  // normalize
  if (audioMarkerStart > audioMarkerEnd) {
    [audioMarkerStart, audioMarkerEnd] = [audioMarkerEnd, audioMarkerStart];
  }

  // Sort entries by audioOffsetMs (ascending). audioOffsetMs is treated as the "beginning" of a bucket.
  const entries = Array.from(managerMap.entries())
    .map(([k, v]) => ({ key: k, value: v }))
    .sort((a, b) => (a.value?.audioOffsetMs ?? 0) - (b.value?.audioOffsetMs ?? 0));

  let bestKey: string | null = null;
  let bestOverlap = -1;

  for (let i = 0; i < entries.length; i++) {
    const current = entries[i];
    const currentOffset = current.value?.audioOffsetMs ?? 0;
    const nextOffset =
      i === entries.length - 1 ? Number.POSITIVE_INFINITY : entries[i + 1].value?.audioOffsetMs;
    const endBound = nextOffset ?? Number.POSITIVE_INFINITY;

    const overlapStart = Math.max(audioMarkerStart, currentOffset);
    const overlapEnd = Math.min(audioMarkerEnd, endBound);
    const overlap = Math.max(0, overlapEnd - overlapStart);

    if (
      overlap > bestOverlap ||
      (overlap === bestOverlap && (bestKey == null || current.key < bestKey))
    ) {
      bestOverlap = overlap;
      bestKey = current.key;
    }
  }

  return bestKey ?? entries[0].key;
};

/**
 * Returns the map entry whose audio-offset bucket contains `onsetMs`, keying on
 * where the marker began rather than on total overlap. Used to keep trailing
 * punctuation with the preceding text: its marker can straddle a modify
 * boundary, and overlap would advance it into the next field.
 *
 * Ties on an equal offset resolve to the smaller map key, and an onset before
 * the earliest bucket falls back to that bucket. Returns null for an empty map.
 */
export const getEntryIdForOnset = (
  managerMap: Map<string, DictationStableTextManagerMapValue>,
  onsetMs: number
): Nullable<string> => {
  if (managerMap.size === 0) return null;

  const entries = Array.from(managerMap.entries())
    .map(([key, value]) => ({ key, offset: value?.audioOffsetMs ?? 0 }))
    .sort((a, b) => a.offset - b.offset || (a.key < b.key ? -1 : 1));

  // Select the last bucket at or before the onset. Advancing only past a
  // strictly greater offset keeps the smaller key when offsets tie.
  let target = entries[0];
  for (const entry of entries) {
    if (entry.offset > onsetMs) break;
    if (entry.offset > target.offset) target = entry;
  }
  return target.key;
};

// A stable-text payload whose text is only punctuation is routed by onset
// rather than overlap; see getEntryIdForOnset.
const TRAILING_PUNCTUATION_ONLY = /^[.,!?;:]+$/;
export const isTrailingPunctuationText = (text: string): boolean =>
  TRAILING_PUNCTUATION_ONLY.test(text.trim());
